import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot,
  Plus,
  Trash2,
  Settings,
  Database,
  MessageSquare,
  Loader2,
  CheckCircle2,
  CircleDashed,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import AgentPageShell, { EmptyState } from '../../components/agent/AgentPageShell';
import { createAgent, deleteAgent } from '../../lib/agentApi';
import { useAgents } from '../../hooks/useAgents';
import { API_URL } from '../../config';

const AgentsPage = () => {
  const navigate = useNavigate();
  const { orgId, agents, meta, loading, refresh, selectAgent } = useAgents();
  const [spaces, setSpaces] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', display_name: '', description: '', space_ids: [] });

  useEffect(() => {
    if (!orgId) return;
    axios
      .get(`${API_URL}/organizations/${orgId}/spaces`)
      .then(({ data }) => setSpaces(data || []))
      .catch(() => setSpaces([]));
  }, [orgId]);

  const toggleSpace = (spaceId) => {
    setForm((prev) => ({
      ...prev,
      space_ids: prev.space_ids.includes(spaceId)
        ? prev.space_ids.filter((s) => s !== spaceId)
        : [...prev.space_ids, spaceId],
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Give the agent a name');
      return;
    }
    setSaving(true);
    try {
      const agent = await createAgent(orgId, form);
      toast.success(`${agent.name} created`);
      setShowCreate(false);
      setForm({ name: '', display_name: '', description: '', space_ids: [] });
      await refresh();
      selectAgent(agent.id);
      navigate('/dashboard/agent/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the agent');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteAgent(orgId, pendingDelete.id);
      toast.success(`${pendingDelete.name} deleted`);
      setPendingDelete(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete the agent');
    }
  };

  const limitLabel =
    meta.limit < 0 ? 'Unlimited' : `${meta.used} of ${meta.limit} used`;

  return (
    <AgentPageShell
      title="Agents"
      description="Each agent reads a chosen set of spaces and deploys to its own surface"
      icon={Bot}
      breadcrumb={['Agents']}
      requireAgent={false}
      actions={
        <Button
          onClick={() => setShowCreate(true)}
          disabled={!meta.canCreate}
          title={meta.canCreate ? undefined : 'Your plan has no remaining agent slots'}
          data-testid="create-agent-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Agent
        </Button>
      }
    >
      {() => (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="font-normal">
              Plan: {meta.planId || 'free'}
            </Badge>
            <Badge variant="secondary" className="font-normal">
              Agents: {limitLabel}
            </Badge>
            {!meta.canCreate && (
              <Link
                to="/dashboard/pricing"
                className="text-xs text-primary font-medium hover:underline"
              >
                Need more agents? Upgrade your plan →
              </Link>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : agents.length === 0 ? (
            <EmptyState
              icon={Bot}
              title="No agents yet"
              body="An agent turns the documentation in your spaces into a voice and text assistant you can embed in your apps."
              action={
                <Button onClick={() => setShowCreate(true)} disabled={!meta.canCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first agent
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {agents.map((agent) => (
                <Card key={agent.id} className="group hover:border-primary/40 transition-colors">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <Bot className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold truncate">{agent.name}</h3>
                          <p className="text-xs text-muted-foreground truncate">
                            {agent.config?.display_name || 'Assistant'}
                          </p>
                        </div>
                      </div>
                      {agent.published_version ? (
                        <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 shrink-0">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Live
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="shrink-0 text-muted-foreground">
                          <CircleDashed className="w-3 h-3 mr-1" />
                          Draft
                        </Badge>
                      )}
                    </div>

                    {agent.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {agent.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Database className="w-3.5 h-3.5" />
                        {agent.space_ids?.length || 0} spaces
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {agent.session_count || 0} sessions
                      </span>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          selectAgent(agent.id);
                          navigate('/dashboard/agent/dashboard');
                        }}
                      >
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          selectAgent(agent.id);
                          navigate('/dashboard/agent/prompt-persona');
                        }}
                        title="Configure"
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setPendingDelete(agent)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Create an agent</DialogTitle>
                <DialogDescription>
                  Pick the spaces this agent may read. Its answers come from those documents
                  and nothing else.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="agent-name">Agent name</Label>
                    <Input
                      id="agent-name"
                      placeholder="Customer Assistant"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agent-display">Display name</Label>
                    <Input
                      id="agent-display"
                      placeholder="Aria"
                      value={form.display_name}
                      onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agent-desc">Description</Label>
                  <Textarea
                    id="agent-desc"
                    placeholder="Answers customer questions inside the mobile app"
                    className="min-h-[70px]"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Knowledge spaces</Label>
                  {spaces.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No spaces in this workspace yet.{' '}
                      <Link to="/dashboard/spaces" className="text-primary hover:underline">
                        Create one first
                      </Link>
                      .
                    </p>
                  ) : (
                    <div className="max-h-44 overflow-y-auto rounded-md border border-border divide-y divide-border agent-scrollbar">
                      {spaces.map((space) => (
                        <label
                          key={space.id}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer"
                        >
                          <Checkbox
                            checked={form.space_ids.includes(space.id)}
                            onCheckedChange={() => toggleSpace(space.id)}
                          />
                          <span className="text-sm flex-1 truncate">{space.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create agent
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <AlertDialog open={Boolean(pendingDelete)} onOpenChange={() => setPendingDelete(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the agent, its configuration history, deployment keys and all
                  of its conversation transcripts. Your documents are untouched. This cannot
                  be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete agent
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </AgentPageShell>
  );
};

export default AgentsPage;
